import { useState } from "react";
import { Button } from "flowbite-react";
import { PurchasePointsErrorState } from "@/features/purchasePoints/components/PurchasePointsErrorState";
import { RewardHistoryRow } from "@/features/purchasePoints/components/RewardHistory/RewardHistoryRow";
import {
  BACK_TO_FIRST_PAGE_LABEL,
  PURCHASE_POINTS_REWARDS_PER_PAGE,
  REWARD_HISTORY_BEYOND_LAST_PAGE_MESSAGE,
  REWARD_HISTORY_EMPTY_MESSAGE,
  REWARD_HISTORY_ERROR_MESSAGE,
  REWARD_HISTORY_TITLE,
} from "@/features/purchasePoints/config/purchasePoints.config";
import { isAccessCode } from "@/features/purchasePoints/errors/purchasePointsError";
import { usePurchasePointsRewards } from "@/features/purchasePoints/hooks/usePurchasePointsRewards";
import { readCatalogPagination } from "@/features/purchasePoints/utils/catalogPagination";
import { Pagination } from "@/shared/components/Pagination";
import { H2 } from "@/shared/ui/typographies/h2";
import { P } from "@/shared/ui/typographies/paragraph";

// Its own page, not the catalogue's `?page=`, which it would repaginate.
const FIRST_PAGE = 1;

const TITLE_ID = "reward-history-title";

export const RewardHistoryList = () => {
  const [page, setPage] = useState(FIRST_PAGE);
  const {
    rewards, metadata, isLoading, isDisabled, error, retry,
  } = usePurchasePointsRewards(page, PURCHASE_POINTS_REWARDS_PER_PAGE);
  const { totalPages, isBeyondLastPage } = readCatalogPagination(metadata, page);

  // No employee attached to the session: nothing was requested, so claiming an
  // empty history would state a fact nobody checked.
  if (isDisabled) return null;

  const isEmpty = !isLoading && !error && rewards.length === 0;

  return (
    <section aria-labelledby={TITLE_ID} className="flex flex-col gap-4" data-testid="reward-history">
      <H2 id={TITLE_ID}>{REWARD_HISTORY_TITLE}</H2>

      {isLoading && (
        <div
          role="status"
          aria-busy="true"
          aria-label={REWARD_HISTORY_TITLE}
          data-testid="reward-history-skeleton"
          className="h-32 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"
        />
      )}

      {!isLoading && error && (
        <div
          data-testid="reward-history-error"
          // An access refusal announces itself politely on its own; only a
          // failure has to interrupt, the skeleton it replaces having taken its
          // live region away with it.
          role={isAccessCode(error.code) ? undefined : "alert"}
        >
          <PurchasePointsErrorState
            error={error}
            onRetry={retry}
            onBackToFirstPage={page > FIRST_PAGE ? () => setPage(FIRST_PAGE) : undefined}
            fallbackMessage={REWARD_HISTORY_ERROR_MESSAGE}
          />
        </div>
      )}

      {isEmpty && (
        <div role="status" className="flex flex-col items-start gap-3" data-testid="reward-history-empty">
          <P>{isBeyondLastPage ? REWARD_HISTORY_BEYOND_LAST_PAGE_MESSAGE : REWARD_HISTORY_EMPTY_MESSAGE}</P>
          {/* The page is local state: without this button neither a reload nor
              the browser's back button brings the employee back to page one. */}
          {isBeyondLastPage && (
            <Button color="light" size="sm" onClick={() => setPage(FIRST_PAGE)}>
              {BACK_TO_FIRST_PAGE_LABEL}
            </Button>
          )}
        </div>
      )}

      {!isLoading && !error && rewards.length > 0 && (
        // Tailwind's preflight strips the bullets, and with them the list role
        // Safari exposes to VoiceOver.
        <ul role="list" className="flex flex-col">
          {rewards.map((reward) => <RewardHistoryRow key={reward.id} reward={reward} />)}
        </ul>
      )}

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        testId="reward-history-pagination"
      />
    </section>
  );
};

RewardHistoryList.displayName = "RewardHistoryList";
